/**
 * 可见渠道账号缓存 - Pinia store
 *
 * /chat-records/accounts 返回当前坐席可见的全部账号，聊天记录、推送、同步记录、各看板、
 * 工作台筛选等多处都要用；账号多时每处各拉一次全量既慢又重复。这里按请求参数缓存，
 * 同参数的在途请求合并，TTL 兜底账号增删；授权页增删账号后主动 invalidate，切换登录账号时同样 invalidate。
 * 缓存只决定是否发请求，不改变请求参数。
 */
import { defineStore } from 'pinia';
/** 直接引具体模块而非 @/api 汇总入口：渠道授权接口会回调本 store 失效缓存，避免循环引用 */
import { chatRecordsApi } from '@/api/chatRecords';

/** 缓存有效期 */
const CACHE_TTL_MS = 5 * 60 * 1000;

/** 参数序列化为缓存 key：按键名排序，忽略 undefined，保证同参数不同书写顺序命中同一份缓存 */
const cacheKeyOf = (params?: Record<string, unknown>) =>
  JSON.stringify(
    Object.keys(params ?? {})
      .sort()
      .filter((k) => params?.[k] !== undefined)
      .map((k) => [k, params?.[k]]),
  );

export const useChatAccountsStore = defineStore('chatAccounts', () => {
  const cache = new Map<string, { list: unknown[]; at: number }>();
  const inflight = new Map<string, Promise<unknown[]>>();
  /** 每次失效递增，阻止失效前发出的请求回写缓存 */
  let version = 0;

  /**
   * 拉取账号列表：缓存有效时直接返回，否则请求（同参数并发只发一次）。
   * 返回数组副本，调用方排序、过滤不影响缓存。
   */
  const fetchAccounts = async <T = unknown>(params?: Record<string, unknown>): Promise<T[]> => {
    const key = cacheKeyOf(params);
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_TTL_MS) return [...hit.list] as T[];
    let pending = inflight.get(key);
    if (!pending) {
      const currentVersion = version;
      pending = chatRecordsApi
        .accounts(params)
        .then((list) => {
          if (currentVersion === version) cache.set(key, { list, at: Date.now() });
          return list;
        })
        .finally(() => {
          if (inflight.get(key) === pending) inflight.delete(key);
        });
      inflight.set(key, pending);
    }
    return [...(await pending)] as T[];
  };

  /** 账号增删、授权变化后调用；切换登录账号时同样调用，丢弃上一个账号的数据 */
  const invalidate = () => {
    version++;
    cache.clear();
    inflight.clear();
  };

  return { fetchAccounts, invalidate };
});
